/**
 * Stone Investment Contact Section
 * 
 * Complete contact section with Stone Investment's luxury aesthetic:
 * - Two-column layout
 * - Contact info + form
 * - Office locations
 */

'use client';

import { MapPin, Phone, Mail } from 'lucide-react';
import { StoneContactFormSection, type ContactFormData } from './StoneContactFormSection';

export interface OfficeLocation {
  name: string;
  address: string;
  phone?: string;
  email?: string;
  region: 'provence' | 'mauritius';
}

export interface StoneContactSectionProps {
  /**
   * Section title
   */
  title?: string;
  /**
   * Section subtitle
   */
  subtitle?: string;
  /**
   * Office locations
   */
  offices?: OfficeLocation[];
  /**
   * General email
   */
  generalEmail?: string;
  /**
   * General phone
   */
  generalPhone?: string;
  /**
   * Form submit handler
   */
  onFormSubmit?: (data: ContactFormData) => Promise<void>;
  /**
   * Translation messages
   */
  messages?: Record<string, string>;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * StoneContactSection - Complete contact section
 */
export function StoneContactSection({
  title = 'Contact Our Team',
  subtitle,
  offices = [],
  generalEmail,
  generalPhone,
  onFormSubmit,
  messages = {},
  className = '',
}: StoneContactSectionProps) {
  return (
    <section className={`py-20 lg:py-32 bg-stone-50 ${className}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Contact Information */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-sans mb-4">
              {messages.eyebrow || 'Get in Touch'}
            </p>
            <h2 
              className="font-serif text-4xl lg:text-5xl text-stone-900 mb-6"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {title}
            </h2>
            
            {subtitle && (
              <p className="text-lg text-stone-600 font-sans mb-12">
                {subtitle}
              </p>
            )}
            
            {/* General Contact */}
            {(generalEmail || generalPhone) && (
              <div className="mb-12 pb-8 border-b border-stone-200">
                <h3 className="font-serif text-xl text-stone-900 mb-6 italic">
                  {messages.generalInquiries || 'General Inquiries'}
                </h3>
                <div className="space-y-4">
                  {generalEmail && (
                    <a 
                      href={`mailto:${generalEmail}`}
                      className="flex items-center gap-3 text-stone-700 hover:text-stone-900 transition-colors"
                    >
                      <Mail size={18} strokeWidth={1.5} className="text-stone-400" />
                      <span className="font-sans">{generalEmail}</span>
                    </a>
                  )}
                  {generalPhone && (
                    <a 
                      href={`tel:${generalPhone.replace(/\s/g, '')}`}
                      className="flex items-center gap-3 text-stone-700 hover:text-stone-900 transition-colors"
                    >
                      <Phone size={18} strokeWidth={1.5} className="text-stone-400" />
                      <span className="font-sans">{generalPhone}</span>
                    </a>
                  )}
                </div>
              </div>
            )}
            
            {/* Office Locations */}
            {offices.length > 0 && (
              <div>
                <h3 className="font-serif text-xl text-stone-900 mb-6 italic">
                  {messages.ourOffices || 'Our Offices'}
                </h3>
                <div className="space-y-8">
                  {offices.map((office, index) => (
                    <div key={index} className="relative pl-6 border-l-2 border-stone-200">
                      {/* Region Flag Colors */}
                      <div className="absolute left-0 top-0 w-0.5 h-6 -ml-0.5">
                        {office.region === 'provence' ? (
                          <div className="h-full flex flex-col">
                            <div className="flex-1 bg-[#002654]" />
                            <div className="flex-1 bg-white" />
                            <div className="flex-1 bg-[#ED2939]" />
                          </div>
                        ) : (
                          <div className="h-full flex flex-col">
                            <div className="flex-1 bg-[#EA2839]" />
                            <div className="flex-1 bg-[#1A206D]" />
                            <div className="flex-1 bg-[#FFD500]" />
                            <div className="flex-1 bg-[#00A551]" />
                          </div>
                        )}
                      </div>
                      
                      <h4 className="font-sans font-medium text-stone-900 mb-2">
                        {office.name}
                      </h4>
                      <p className="text-sm text-stone-600 font-sans mb-3 flex items-start gap-2">
                        <MapPin size={14} className="text-stone-400 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                        {office.address}
                      </p>
                      {office.phone && (
                        <a 
                          href={`tel:${office.phone.replace(/\s/g, '')}`}
                          className="text-sm text-stone-600 hover:text-stone-900 font-sans flex items-center gap-2 mb-1"
                        >
                          <Phone size={14} className="text-stone-400" strokeWidth={1.5} />
                          {office.phone}
                        </a>
                      )}
                      {office.email && (
                        <a 
                          href={`mailto:${office.email}`}
                          className="text-sm text-stone-600 hover:text-stone-900 font-sans flex items-center gap-2"
                        >
                          <Mail size={14} className="text-stone-400" strokeWidth={1.5} />
                          {office.email}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Contact Form */}
          <div className="bg-white p-8 lg:p-12">
            <StoneContactFormSection
              title={messages.formTitle || 'Send a Message'}
              onSubmit={onFormSubmit || (async () => {})}
              messages={messages}
              className="py-0"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default StoneContactSection;
